import { toast } from "sonner";

/**
 * Toast Hook
 * Provides a simple interface for showing toast notifications
 */
export const useToast = () => {
  const showToast = (message, options = {}) => {
    const {
      type = "default",
      title,
      description,
      duration = 4000,
      action,
      cancel,
      ...rest
    } = options;

    const toastOptions = {
      duration,
      ...rest,
    };

    // Add action and cancel if provided
    if (action) {
      toastOptions.action = action;
    }
    if (cancel) {
      toastOptions.cancel = cancel;
    }

    // Add title and description if provided
    if (title) {
      toastOptions.description = description || message;
      return toast[type](title, toastOptions);
    }

    if (description) {
      toastOptions.description = description;
    }

    return toast[type](message, toastOptions);
  };

  return {
    // Generic showToast method for backward compatibility
    showToast: (message, type = "default", options = {}) =>
      showToast(message, { ...options, type }),

    // Success toast
    success: (message, options = {}) =>
      showToast(message, { ...options, type: "success" }),

    // Error toast
    error: (message, options = {}) =>
      showToast(message, { ...options, type: "error" }),

    // Warning toast
    warning: (message, options = {}) =>
      showToast(message, { ...options, type: "warning" }),

    // Info toast
    info: (message, options = {}) =>
      showToast(message, { ...options, type: "info" }),

    // Default toast
    default: (message, options = {}) =>
      showToast(message, { ...options, type: "default" }),

    // Loading toast
    loading: (message, options = {}) =>
      showToast(message, { ...options, type: "loading" }),

    // Promise toast
    promise: (promise, options = {}) => toast.promise(promise, options),

    // Dismiss toast
    dismiss: (toastId) => toast.dismiss(toastId),

    // Dismiss all toasts
    dismissAll: () => toast.dismiss(),
  };
};

export default useToast;
